import { useState, useEffect, useRef, useMemo } from "react";
import { CurrencyDollar, Plus, ArrowsClockwise, Receipt, Calendar, Warning, CheckCircle, Trash, Lock, MagnifyingGlass, FileArrowUp } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  InitialSpecificationPanel, AmendmentsPanel, RenewalHistoryPanel,
  CurrentSpecSummary, SourceDocumentsPanel,
} from "./BillingSpecificationPanels";
import { CommercialStatusBar } from "./BillingWorkspaceParts";
import Eba1ReconciliationPanel from "./Eba1ReconciliationPanel";
import { GenerateProposalDialog } from "./RenewalProposalDialog";
import InvoiceImportWizard from "./InvoiceImportWizard";
import { rollUpArr } from "@/lib/arrRollUp";
import {
  invoiceDateOf, canPermanentlyDelete, proposalAvailability,
} from "@/lib/billingProvenance";

// v1.50 — Recurring-item per-line discount support per Mike's spec:
//   • New `discount_pct` field (0-100%) on the Add/Edit form.
//   • Live preview shows gross, discount, net per period + annualised.
//   • Table gains a Discount column; Line total and Annualised are NET
//     (after the discount); Cumulative is the running NET ARR.
//   • Top of the screen now has 3 ARR tiles: Gross / Total Discounts /
//     Net ARR — plus a "Net ARR (after discounts)" footer row in the
//     Recurring Items table.
// v1.49 — Recurring-item screen behavioural fixes per Mike:
//   • DELETE now hard-removes the row (no more 'cancelled' ghost rows).
//   • Click anywhere on a row to open the edit form pre-filled with
//     the current values; saving calls PATCH instead of POST.
// v1.48 — Recurring-item form upgrades per Mike's spec:
//   • Description is now a typeahead lookup against mgmt_products (the
//     items database). Free-text editing is allowed as a fallback for
//     ad-hoc items that don't have a product row yet, but the standard
//     workflow is "type → pick → autofill".
//   • billing_period dropdown: one-time / month / year
//   • account dropdown: Recurring revenue / Professional services /
//     Training / Software development / Consulting / One-time charge
//   • Optional original_invoice_date + original_invoice_number to
//     anchor the recurring item to the historical first invoice.
const BILLING_PERIODS = [
  { value: "one_time", label: "One time" },
  { value: "month",    label: "Month" },
  { value: "year",     label: "Year" },
];
const ACCOUNTS = [
  "Recurring",
  "Professional services",
  "Training",
  "Software development",
  "Consulting",
  "One-time charge",
];

// Annualise per-period: month → ×12, year → ×1, one_time → ×0 (not ARR).
const annualMultiplier = (period) =>
  ({ month: 12, year: 1, one_time: 0 }[period] ?? 1);

/**
 * The Current Recurring Specification — what the client is licensed for
 * TODAY, and the basis the next renewal starts from. This is the live,
 * mutable ledger (`client_recurring_items`); the other three commercial
 * surfaces live in BillingSpecificationPanels.js.
 */
function CurrentRecurringSpecificationPanel({ clientId, isAdmin, onImportInvoice }) {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [preview, setPreview] = useState(null);
  // Renewal readiness drives whether a proposal can be offered at all —
  // the same guard the backend enforces, so the UI never advertises an
  // action the server will refuse.
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showProposal, setShowProposal] = useState(false);   // v2.01 Generate Proposal
  const [editingId, setEditingId] = useState(null);   // v1.49 — inline edit mode
  const [invoiceDateSort, setInvoiceDateSort] = useState("desc");  // v1.52 — clickable header sort, newest first by default
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    description: "",
    product_id: null,
    quantity: 1,
    unit_price_annual: 0,
    billing_period: "year",
    account: "Recurring",
    // v1.51 — "Effective from" removed per Mike's request. The
    // (now-required) invoice date doubles as the activation date and
    // is sent server-side as `effective_from` as well.
    original_invoice_date: "",
    original_invoice_number: "",
    discount_pct: 0,
    prorate: true,
    notes: "",
  });

  // Product typeahead state
  const [products, setProducts] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ri, ev, pv, rd] = await Promise.all([
        api.get(`/billing/clients/${clientId}/recurring-items`),
        api.get(`/billing/clients/${clientId}/events`),
        api.post(`/billing/clients/${clientId}/renewal/preview`, {}).catch(() => ({ data: null })),
        api.get(`/renewals/clients/${clientId}/readiness`).catch(() => ({ data: null })),
      ]);
      setItems(ri.data.items || []);
      setEvents(ev.data.events || []);
      setPreview(pv.data);
      setReadiness(rd.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load billing");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [clientId]);

  // Lazy-load the products catalog the first time the operator opens
  // the Add Item form. Cached in state so subsequent opens are
  // instant. Endpoint: `GET /api/mgmt/products?is_active=true`.
  useEffect(() => {
    if (!showAdd || products.length > 0) return;
    api.get("/mgmt/products?is_active=true&limit=500")
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setProducts(list);
      })
      .catch(() => { /* quiet — free-text fallback still works */ });
  }, [showAdd, products.length]);

  const filteredProducts = useMemo(() => {
    const q = (productQuery || "").trim().toLowerCase();
    if (!q) return products.slice(0, 20);
    return products
      .filter(p =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.product_code || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [products, productQuery]);

  const pickProduct = (p) => {
    // Autofill description + unit price from the chosen product so the
    // operator only has to type the qty, billing period, and account.
    // v1.53 — "Supplementary cost" items are by policy annual recurring
    // charges (annualised cost = unit price), so we force the billing
    // period to "year" whenever that product is picked. The operator
    // can still override it manually if they really want to.
    const isSupplementary = (p.name || "").toLowerCase().includes("supplementary cost");
    setForm(f => ({
      ...f,
      description: p.name,
      product_id: p.product_id,
      unit_price_annual: p.current_selling_price ?? f.unit_price_annual,
      billing_period: isSupplementary ? "year" : f.billing_period,
    }));
    setProductQuery(p.name);
    setShowProductDropdown(false);
  };

  const resetForm = () => {
    setForm({
      description: "", product_id: null, quantity: 1, unit_price_annual: 0,
      billing_period: "year", account: "Recurring",
      original_invoice_date: "", original_invoice_number: "",
      discount_pct: 0, prorate: true, notes: "",
    });
    setProductQuery("");
    setEditingId(null);
  };

  // v1.49 — open the form in EDIT mode, pre-filled with this row's
  // current values. Triggered by clicking anywhere on the row.
  const startEdit = (it) => {
    if (!isAdmin) return;
    setForm({
      description: it.description || "",
      product_id: it.product_id || null,
      quantity: Number(it.quantity) || 1,
      unit_price_annual: Number(it.unit_price_annual) || 0,
      billing_period: (it.billing_period || "year").toLowerCase(),
      account: it.account || "Recurring",
      // Pre-fill invoice date from either column for backwards-compat
      // with older rows that only have `effective_from`.
      original_invoice_date: (it.original_invoice_date || it.effective_from || "").slice(0, 10),
      original_invoice_number: it.original_invoice_number || "",
      discount_pct: Number(it.discount_pct) || 0,
      // Editing an existing row never re-prorates — that draft (if any)
      // was already issued at create-time. Hide/disable the checkbox.
      prorate: false,
      notes: it.notes || "",
    });
    setProductQuery(it.description || "");
    setEditingId(it.id);
    setShowAdd(true);
  };

  const saveItem = async () => {
    if (!form.description.trim()) { toast.error("Description required — pick an item from the database"); return; }
    if (!form.original_invoice_date) { toast.error("Invoice date is required"); return; }
    if (Number(form.unit_price_annual) <= 0) { toast.error("Unit price must be > 0"); return; }
    const dp = Number(form.discount_pct) || 0;
    if (dp < 0 || dp > 100) { toast.error("Discount must be between 0 and 100"); return; }
    setBusy(true);
    try {
      const payload = {
        description: form.description,
        product_id: form.product_id || null,
        quantity: Number(form.quantity),
        unit_price_annual: Number(form.unit_price_annual),
        billing_period: form.billing_period,
        account: form.account || null,
        // v1.51 — invoice date doubles as the activation (`effective_from`) date.
        effective_from: form.original_invoice_date,
        original_invoice_date: form.original_invoice_date,
        original_invoice_number: form.original_invoice_number || null,
        discount_pct: dp,
        prorate: form.prorate,
        notes: form.notes || null,
      };
      if (editingId) {
        await api.patch(`/billing/clients/${clientId}/recurring-items/${editingId}`, payload);
        toast.success("Recurring item updated.");
      } else {
        const r = await api.post(`/billing/clients/${clientId}/recurring-items`, payload);
        // Backend returns a friendly proration_warning when the row
        // was saved but the FinOps prorated invoice couldn't be
        // drafted (e.g. client not mirrored in FinOps yet). Surface
        // it as a warning toast, NOT an error — the row IS saved.
        if (r.data.proration_warning) {
          toast.warning(r.data.proration_warning, { duration: 8000 });
        } else if (r.data.proration_invoice) {
          toast.success(`Prorated invoice ${r.data.proration_invoice.invoice_number} drafted — $${r.data.proration_invoice.amount.toFixed(2)} for ${r.data.proration_invoice.days} days.`);
        } else {
          toast.success("Recurring item added.");
        }
      }
      setShowAdd(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const deleteItem = async (id, desc, e) => {
    // Row-click opens edit, so a button click here must NOT bubble.
    if (e) e.stopPropagation();
    if (!window.confirm(`Permanently delete recurring item "${desc}"? This cannot be undone — the row will be removed entirely from this client.`)) return;
    try {
      await api.delete(`/billing/clients/${clientId}/recurring-items/${id}`);
      toast.success("Recurring item deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  // v1.66 — Replace the old `window.confirm` + system-anniv-only flow
  // with a modal that lets Mike override the anniversary date.
  // Defaults to `preview.anniversary` (the system-computed one), but a
  // calendar picker lets him pro-forma any future date — backend
  // already accepts `{anniversary: 'YYYY-MM-DD'}`.
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [renewalDate, setRenewalDate] = useState("");
  const [renewalIssueDate, setRenewalIssueDate] = useState("");
  const [renewalSubmitting, setRenewalSubmitting] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  const openRenewalModal = () => {
    setRenewalDate(preview?.anniversary || "");
    setRenewalIssueDate(new Date().toISOString().slice(0, 10));
    setDiagnosis(null);
    setRenewalOpen(true);
  };

  const runDiagnose = async () => {
    if (!renewalDate) {
      toast.warning("Pick an anniversary date first");
      return;
    }
    setDiagnosing(true);
    setDiagnosis(null);
    try {
      const body = { anniversary: renewalDate };
      if (renewalIssueDate) body.issue_date = renewalIssueDate;
      const r = await api.post(
        `/billing/clients/${clientId}/renewal/diagnose`, body,
      );
      setDiagnosis(r.data);
    } catch (e) {
      // Even if diagnose itself 500s, surface the message — that's
      // itself a useful signal (e.g. "endpoint not deployed yet").
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      setDiagnosis({
        ok: false,
        failed_at: "diagnose_endpoint_unreachable",
        error: detail
          ? `[HTTP ${status}] ${detail}`
          : `[HTTP ${status || "?"}] ${e.message || "unknown"}`,
        steps: [],
        code_build: "(unknown — endpoint not reachable)",
      });
    } finally {
      setDiagnosing(false);
    }
  };

  const submitRenewal = async () => {
    if (!renewalDate) {
      toast.warning("Pick an anniversary date");
      return;
    }
    setRenewalSubmitting(true);
    try {
      const body = { anniversary: renewalDate };
      if (renewalIssueDate) body.issue_date = renewalIssueDate;
      const r = await api.post(
        `/billing/clients/${clientId}/renewal/generate`, body,
      );
      toast.success(
        `Renewal ${r.data.invoice_number} drafted — $${r.data.subtotal.toFixed(2)}`
      );
      setRenewalOpen(false);
      load();
    } catch (e) {
      // Surface the full server reason — earlier toast fallback hid
      // useful info like 409 "already drafted for 2026-07-11" or 500
      // stack-trace summaries. If the server responded with structured
      // JSON, show the `detail` + HTTP status; otherwise show the raw
      // error message so the operator can copy/paste it back.
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const msg = detail
        ? (status ? `[HTTP ${status}] ${detail}` : detail)
        : (e.message || "Generate failed");
      toast.error(msg, { duration: 10000 });
    } finally {
      setRenewalSubmitting(false);
    }
  };

  const activeItems = items.filter(i => i.status === "active");
  const { grossArr, creditTotal, annualTotal } = rollUpArr(activeItems);
  const currency = activeItems[0]?.currency_code || "USD";

  if (loading) return <div className="p-8 text-center text-[#A3A3A3] text-xs">Loading billing…</div>;

  return (
    <div className="space-y-5" data-testid="client-billing-tab">
      {/* The banner and the Generate Proposal card below read the SAME
          readiness object. They cannot disagree, and validating the
          baseline reloads this panel so both update together. */}
      <CommercialStatusBar clientId={clientId} isAdmin={isAdmin}
                           readiness={readiness} onValidated={load} />
      <CurrentSpecSummary clientId={clientId} />
      {/* v1.50 — ARR roll-up with discount surfacing. Three tiles:
          Gross ARR · Total Discounts · Net ARR. Hidden if no active items. */}
      {activeItems.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="billing-arr-strip">
          <div className="border border-[#E5E5E5] bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Gross ARR</div>
            <div className="text-xl font-black text-[#525252] mt-1 tracking-tight line-through-on-discount" data-testid="billing-arr-gross">
              {currency} {grossArr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-[11px] text-[#A3A3A3] mt-0.5">Before credits &amp; discounts</div>
          </div>
          <div className="border border-[#E5E5E5] bg-white p-4">
            {/* Renamed from "Total Discounts": a signed credit line is a
                first-class commercial line, not a percentage discount. */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Recurring Credits / Discounts</div>
            <div className={`text-xl font-black mt-1 tracking-tight ${creditTotal < 0 ? "text-[#DC2626]" : "text-[#A3A3A3]"}`} data-testid="billing-arr-discount">
              {currency} {creditTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-[11px] text-[#A3A3A3] mt-0.5">
              {(() => {
                const credits = activeItems.filter(i => (Number(i.annual_amount) || 0) < 0).length;
                const discounted = activeItems.filter(i => Number(i.discount_pct) > 0).length;
                const bits = [];
                if (credits) bits.push(`${credits} credit line${credits !== 1 ? "s" : ""}`);
                if (discounted) bits.push(`${discounted} discounted line${discounted !== 1 ? "s" : ""}`);
                return bits.length ? bits.join(" · ") : "None";
              })()}
            </div>
          </div>
          <div className="border-2 border-[#0055FF] bg-[#EFF6FF] p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF]">Net ARR (after credits)</div>
            <div className="text-2xl font-black text-[#0055FF] mt-1 tracking-tight" data-testid="billing-arr-net">
              {currency} {annualTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-[11px] text-[#525252] mt-0.5">{activeItems.length} active item{activeItems.length !== 1 ? "s" : ""}</div>
          </div>
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-[#E5E5E5] bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Annual Recurring Revenue</div>
          <div className="text-2xl font-black text-[#0A0A0A] mt-1 tracking-tight" data-testid="billing-arr">{currency} {annualTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="text-[11px] text-[#525252] mt-0.5">{activeItems.length} active item{activeItems.length !== 1 ? "s" : ""}{creditTotal < 0 ? ` · net of ${currency} ${Math.abs(creditTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} credits` : ""}</div>
        </div>
        {/* v2.01 — the contractual term, not a date derived from
            client_since. Once a baseline exists the active specification
            states the period outright, so it is authoritative; the
            backend reports which source it used. */}
        <div className="border border-[#E5E5E5] bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
            {preview?.current_term_end ? "Current Term Expires" : "Next Anniversary"}
          </div>
          <div className="text-2xl font-black text-[#0A0A0A] mt-1 tracking-tight" data-testid="billing-anniversary">
            {preview?.current_term_end || preview?.anniversary || "—"}
          </div>
          {preview?.next_term_start ? (
            <div className="text-[11px] text-[#525252] mt-0.5" data-testid="billing-next-term">
              Next term begins {preview.next_term_start}
              {preview.anniversary_source === "active_specification"
                ? " · from the active specification"
                : " · derived from client since"}
            </div>
          ) : preview ? (
            <div className="text-[11px] text-[#525252] mt-0.5">{preview.line_count} item{preview.line_count !== 1 ? "s" : ""} · {currency} {Number(preview.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          ) : (
            <div className="text-[11px] text-[#A3A3A3] mt-0.5">No commercial period set</div>
          )}
        </div>
        <div className="border border-[#E5E5E5] bg-white p-4 flex flex-col justify-between">
          <div>
            {/* Pulse does not raise invoices — FinOps does. This surface
                produces a customer PROPOSAL and nothing else. */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Renewal Proposal</div>
            <div className="text-[11px] text-[#525252] mt-1">
              Generate a proposal from this specification. Nothing is sent until you send it.
            </div>
          </div>
          {(() => {
            // Offering "Generate Proposal" beside a RENEWAL HELD banner
            // is a contradiction the operator has to resolve by clicking
            // and being refused. Ask the same guard the server uses.
            const { allowed, reason } = proposalAvailability({
              readiness, hasActiveItems: activeItems.length > 0, isAdmin,
            });
            if (allowed) {
              return (
                <Button onClick={() => setShowProposal(true)}
                        className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs mt-2"
                        data-testid="generate-proposal-btn">
                  <Receipt size={13} className="mr-1.5" /> Generate Proposal
                </Button>
              );
            }
            if (!isAdmin || !reason) return null;
            return (
              <div className="mt-2" data-testid="generate-proposal-held">
                <Button disabled
                        className="rounded-none bg-[#F5F5F5] text-[#A3A3A3] text-xs cursor-not-allowed w-full"
                        data-testid="generate-proposal-btn-disabled">
                  <Receipt size={13} className="mr-1.5" /> Generate Proposal
                </Button>
                <p className="text-[11px] text-[#92400E] mt-1.5 leading-snug">{reason}</p>
              </div>
            );
          })()}
        </div>
      </div>

      <GenerateProposalDialog
        open={showProposal}
        onOpenChange={setShowProposal}
        clientId={clientId}
        currency={currency}
        currentArr={annualTotal}
        onPrepared={load}
      />

      {/* eBA 1.0 reconciliation. Sits directly under the Generate Proposal
          card on purpose: when the renewal is held on a licensed-user
          quantity, the reason and the way to resolve it must be adjacent.
          Confirming reloads this tab so the readiness banner and the
          Generate Proposal button move together rather than the screen
          contradicting itself until someone refreshes. */}
      <Eba1ReconciliationPanel clientId={clientId} isAdmin={isAdmin} onConfirmed={load} />

      {/* Recurring items */}
      <div className="border border-[#E5E5E5] bg-white">
        <div className="bg-[#FAFAFA] px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-[#0A0A0A]">Recurring Items</h3>
            <p className="text-[10px] text-[#A3A3A3] mt-0.5">Each active item is billed every anniversary. New items added mid-year are prorated to the next anniversary.{isAdmin ? " Click any row to edit it." : ""}</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              {/* Recording an invoice the customer has ALREADY received is
                  a different act from adding a new charge: it must never
                  produce a proration draft. It gets its own entry point
                  so the two can never be confused at the keyboard. */}
              <Button
                onClick={onImportInvoice}
                variant="outline"
                className="rounded-none text-xs"
                title="Record an invoice that has already been sent to this client"
                data-testid="import-invoice-btn"
              >
                <FileArrowUp size={13} className="mr-1.5" /> Import Invoice
              </Button>
            <Button
              onClick={() => {
                // Toggle: if open in edit mode → close & reset; otherwise open fresh Add form.
                if (showAdd) { setShowAdd(false); resetForm(); }
                else { resetForm(); setShowAdd(true); }
              }}
              className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white text-xs"
              data-testid="add-recurring-item-btn"
            >
              <Plus size={13} className="mr-1.5" /> {editingId ? "Close" : "Add Item"}
            </Button>
            </div>
          )}
        </div>

        {showAdd && (
          <div className="p-4 bg-[#FAFBFF] border-b border-[#E5E5E5] grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="add-recurring-form">
            <div className="md:col-span-12 -mb-1 text-[11px] font-bold uppercase tracking-wider text-[#525252]">
              {editingId ? "Editing existing item" : "Add new recurring item"}
            </div>
            {/* Product typeahead — replaces the old free-text description.
                Type → filtered list → click → autofills description + price. */}
            <div className="md:col-span-4 relative">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Item (from products database)</Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                <Input
                  ref={productInputRef}
                  value={productQuery}
                  onChange={e => {
                    const val = e.target.value;
                    setProductQuery(val);
                    setShowProductDropdown(true);
                    // v1.53 — Typeahead → description mirroring is now
                    // "smart": only auto-update the description if the
                    // operator hasn't customised it yet (it still
                    // matches the previous typeahead value, or is
                    // empty). That way picking "Training Module" →
                    // editing description to "Bespoke ACME training"
                    // → tweaking a typo in the typeahead won't blow
                    // away the customised label.
                    setForm(f => {
                      const untouched = !f.description || f.description === productQuery;
                      return {
                        ...f,
                        product_id: null,
                        description: untouched ? val : f.description,
                      };
                    });
                  }}
                  onFocus={() => setShowProductDropdown(true)}
                  onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                  className="rounded-none text-xs pl-7"
                  placeholder="Start typing — e.g. Contract Module, Customization, Training"
                  data-testid="recurring-product-search"
                />
              </div>
              {showProductDropdown && (
                <div className="absolute z-20 left-0 right-0 mt-0.5 max-h-64 overflow-y-auto bg-white border border-[#E5E5E5] shadow-lg" data-testid="recurring-product-dropdown">
                  {filteredProducts.length === 0 && (
                    <div className="px-3 py-2 text-[11px] text-[#A3A3A3] italic">
                      No matching products. The text you typed will be saved as a custom description.
                    </div>
                  )}
                  {filteredProducts.map(p => (
                    <button
                      type="button"
                      key={p.product_id}
                      onMouseDown={() => pickProduct(p)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-[#F5F5F5] border-b border-[#F5F5F5] last:border-b-0"
                      data-testid={`recurring-product-option-${p.product_id}`}
                    >
                      <div className="font-bold text-[#0A0A0A]">{p.name}</div>
                      <div className="text-[10px] text-[#A3A3A3] flex items-center gap-2 mt-0.5">
                        <span className="font-mono">{p.product_code}</span>
                        {p.category && <span>· {p.category}</span>}
                        {p.current_selling_price != null && (
                          <span>· {(p.price_currency_code || "USD")} {Number(p.current_selling_price).toFixed(2)}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-1">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Qty</Label>
              <Input type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} className="rounded-none mt-1 text-xs" data-testid="recurring-qty" />
            </div>

            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Unit price</Label>
              <Input type="number" min="0" step="0.01" value={form.unit_price_annual} onChange={e => setForm({ ...form, unit_price_annual: e.target.value })} className="rounded-none mt-1 text-xs" data-testid="recurring-price" />
            </div>

            <div className="md:col-span-1">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Disc %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.discount_pct}
                onChange={e => setForm({ ...form, discount_pct: e.target.value })}
                className="rounded-none mt-1 text-xs"
                data-testid="recurring-discount-pct"
                title="Percentage discount (0–100) applied to this line"
              />
            </div>

            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Per</Label>
              <select
                value={form.billing_period}
                onChange={e => setForm({ ...form, billing_period: e.target.value })}
                className="w-full mt-1 border border-[#E5E5E5] px-2 py-1.5 text-xs h-[34px]"
                data-testid="recurring-billing-period"
              >
                {BILLING_PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Account</Label>
              <select
                value={form.account}
                onChange={e => setForm({ ...form, account: e.target.value })}
                className="w-full mt-1 border border-[#E5E5E5] px-2 py-1.5 text-xs h-[34px]"
                data-testid="recurring-account"
              >
                {ACCOUNTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            {/* v1.53 — Separate free-format Description field. Defaults
                to whatever the operator typed / picked in the typeahead
                above, but is independently editable so the saved/billed
                label can be customised per line (e.g. picked product
                "Training Module" → description "Bespoke training for
                ACME Q2 2026"). The `product_id` link is preserved. */}
            <div className="md:col-span-12">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">
                Description <span className="text-[#A3A3A3]">(editable — defaults from item picker above)</span>
              </Label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="rounded-none mt-1 text-xs"
                placeholder="Defaults to the item name — override here if you need a customised label for this line"
                data-testid="recurring-description"
              />
            </div>

            <div className="md:col-span-3">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">
                Invoice date <span className="text-[#DC2626]">*</span>
              </Label>
              <Input
                type="date"
                required
                value={form.original_invoice_date}
                onChange={e => setForm({ ...form, original_invoice_date: e.target.value })}
                className="rounded-none mt-1 text-xs"
                data-testid="recurring-invoice-date"
                title="Date of the (first) invoice this recurring item is anchored to — also used as the activation date"
              />
            </div>

            <div className="md:col-span-3">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">
                Invoice # <span className="text-[#A3A3A3]">(optional)</span>
              </Label>
              <Input
                value={form.original_invoice_number}
                onChange={e => setForm({ ...form, original_invoice_number: e.target.value })}
                className="rounded-none mt-1 text-xs"
                placeholder="e.g. INV-2024-0148"
                data-testid="recurring-invoice-num"
              />
            </div>

            <div className="md:col-span-3 flex flex-col">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Prorate now?</Label>
              {editingId ? (
                <div className="mt-2 text-[11px] text-[#A3A3A3] italic" data-testid="recurring-prorate-disabled">
                  Not available when editing — original draft (if any) is preserved.
                </div>
              ) : (
                <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.prorate} onChange={e => setForm({ ...form, prorate: e.target.checked })} data-testid="recurring-prorate" />
                  Draft prorated invoice
                </label>
              )}
            </div>

            {/* Live line-total preview so the operator sees the math
                before saving. v1.50: also surfaces per-line discount
                so the operator sees the net amount being charged. */}
            <div className="md:col-span-12 border border-dashed border-[#BFDBFE] bg-[#EFF6FF] px-3 py-2 text-[11px] text-[#1E3A8A]" data-testid="recurring-line-preview">
              {(() => {
                const qty = Number(form.quantity) || 0;
                const price = Number(form.unit_price_annual) || 0;
                const disc = Math.min(100, Math.max(0, Number(form.discount_pct) || 0));
                const period = form.billing_period;
                const mult = annualMultiplier(period);
                const grossLine = qty * price;
                const lineDiscount = grossLine * (disc / 100);
                const netLine = grossLine - lineDiscount;
                const grossAnnual = grossLine * mult;
                const annualDiscount = grossAnnual * (disc / 100);
                const netAnnual = grossAnnual - annualDiscount;
                const periodLabel = BILLING_PERIODS.find(p => p.value === period)?.label || period;
                const periodWord = periodLabel.toLowerCase();
                return (
                  <>
                    Line: <strong>{qty.toLocaleString()}</strong>{" "}× <strong>${price.toFixed(2)}</strong> / {periodWord} ={" "}
                    <strong>${grossLine.toFixed(2)} gross</strong>
                    {disc > 0 && (
                      <>
                        {" "}· <span className="text-[#DC2626]">−${lineDiscount.toFixed(2)} ({disc}% off)</span> ={" "}
                        <strong>${netLine.toFixed(2)} net per {periodWord}</strong>
                      </>
                    )}
                    {disc === 0 && <> per {periodWord}</>}
                    {period !== "one_time" && (
                      <>
                        {" "}· Annualised:{" "}
                        {disc > 0 ? (
                          <>
                            <span className="line-through text-[#A3A3A3]">${grossAnnual.toFixed(2)}</span>{" "}
                            <strong>${netAnnual.toFixed(2)}/yr</strong>
                          </>
                        ) : (
                          <strong>${grossAnnual.toFixed(2)}/yr</strong>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="md:col-span-12">
              <Label className="text-[10px] font-bold uppercase text-[#525252]">Notes (optional)</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-1 text-xs" data-testid="recurring-notes" />
            </div>
            <div className="md:col-span-12 flex justify-end gap-2">
              <Button onClick={() => { setShowAdd(false); resetForm(); }} variant="outline" className="rounded-none text-xs">Cancel</Button>
              <Button onClick={saveItem} disabled={busy} className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs" data-testid="save-recurring-item-btn">
                {busy ? "Saving…" : (editingId ? "Update Item" : "Save")}
              </Button>
            </div>
          </div>
        )}

        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
              <th
                className={`px-3 py-2 text-left font-bold ${isAdmin ? "cursor-pointer hover:bg-[#F0F0F0] select-none" : ""}`}
                onClick={() => setInvoiceDateSort(d => d === "asc" ? "desc" : "asc")}
                title="Click to sort by invoice date"
                data-testid="recurring-sort-invoice-date"
              >
                Invoice date{" "}
                <span className="text-[9px] text-[#0055FF]">
                  {invoiceDateSort === "asc" ? "↑" : "↓"}
                </span>
              </th>
              <th className="px-3 py-2 text-left font-bold">Term from</th>
              <th className="px-3 py-2 text-left font-bold">Invoice #</th>
              <th className="px-3 py-2 text-left font-bold">Item</th>
              <th className="px-3 py-2 text-right font-bold">Qty</th>
              <th className="px-3 py-2 text-right font-bold">Unit price</th>
              <th className="px-3 py-2 text-left font-bold">Per</th>
              <th className="px-3 py-2 text-left font-bold">Account</th>
              <th className="px-3 py-2 text-right font-bold">Disc %</th>
              <th className="px-3 py-2 text-right font-bold">Line total</th>
              <th className="px-3 py-2 text-right font-bold">Annualised</th>
              <th className="px-3 py-2 text-right font-bold">Cumulative</th>
              <th className="px-3 py-2 text-left font-bold">Status</th>
              {isAdmin && <th className="px-3 py-2 text-right font-bold"></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={isAdmin ? 14 : 13} className="px-3 py-8 text-center text-[#A3A3A3]">No recurring items yet. Convert an accepted proposal to seed them automatically.</td></tr>
            )}
            {(() => {
              // v1.52 — sort the rows by invoice date per the
              // clickable header. Items without an invoice date fall
              // back to `effective_from` (which migrated rows still
              // carry). Sort is stable: a copy is mutated, the
              // original `items` array is untouched.
              const sorted = [...items].sort((a, b) => {
                const da = (a.original_invoice_date || a.effective_from || "");
                const db = (b.original_invoice_date || b.effective_from || "");
                // Empty dates sort to the bottom regardless of direction.
                if (!da && !db) return 0;
                if (!da) return 1;
                if (!db) return -1;
                return invoiceDateSort === "asc"
                  ? da.localeCompare(db)
                  : db.localeCompare(da);
              });
              // Cumulative running total across ACTIVE rows only — the
              // cancelled rows still render but don't contribute to the
              // ARR roll-up shown on the far-right column.
              let runningArr = 0;
              const moneyFmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return sorted.map(it => {
                const qty = Number(it.quantity) || 0;
                const price = Number(it.unit_price_annual) || 0;
                const period = (it.billing_period || "year").toLowerCase();
                const periodLabel = BILLING_PERIODS.find(p => p.value === period)?.label || period;
                const disc = Number(it.discount_pct) || 0;
                // Backend now returns NET line_amount / annual_amount + gross + discount.
                const lineGross = Number(it.line_amount_gross ?? qty * price);
                const lineNet = Number(it.line_amount ?? lineGross);
                const annualNet = Number(it.annual_amount ?? lineNet * annualMultiplier(period));
                if (it.status === "active") runningArr += annualNet;
                // An invoice date is the date an invoice was issued. A
                // period start is not one, so falling back to
                // `effective_from` here labelled a term start as an
                // invoice date — and gave reconstructed history, which
                // has no invoice at all, a date that looked documentary.
                // The term is now shown in its own column instead.
                const invDate = invoiceDateOf(it);
                const termFrom = it.effective_from ? String(it.effective_from).slice(0, 10) : "";
                const invNum = it.original_invoice_number || "";
                return (
                  <tr
                    key={it.id}
                    onClick={() => startEdit(it)}
                    className={`border-b border-[#F5F5F5] ${it.status !== "active" ? "opacity-50" : ""} ${isAdmin ? "cursor-pointer hover:bg-[#F8FAFF]" : ""} ${editingId === it.id ? "bg-[#EFF6FF]" : ""}`}
                    title={isAdmin ? "Click to edit this line" : undefined}
                    data-testid={`recurring-row-${it.id}`}
                  >
                    <td className="px-3 py-2 text-[#525252] whitespace-nowrap" data-testid={`invoice-date-${it.id}`}>
                      {invDate || <span className="text-[#A3A3A3]">Not documented</span>}
                    </td>
                    <td className="px-3 py-2 text-[#525252] whitespace-nowrap font-mono">
                      {termFrom || <span className="text-[#A3A3A3]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[#525252] font-mono">
                      {invNum || <span className="text-[#A3A3A3]">—</span>}
                    </td>
                    <td className="px-3 py-2 font-medium text-[#0A0A0A]">{it.description}</td>
                    <td className="px-3 py-2 text-right">{qty.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-[#525252]">{moneyFmt(price)}</td>
                    <td className="px-3 py-2 text-[#525252]">{periodLabel}</td>
                    <td className="px-3 py-2 text-[#525252]">
                      {it.account
                        ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-[#F5F5F5] text-[#525252] border border-[#E5E5E5]">{it.account}</span>
                        : <span className="text-[#A3A3A3] italic">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {disc > 0
                        ? <span className="text-[#DC2626] font-bold">{disc}%</span>
                        : <span className="text-[#A3A3A3]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {disc > 0 ? (
                        <div className="leading-tight">
                          <div className="text-[10px] text-[#A3A3A3] line-through">{moneyFmt(lineGross)}</div>
                          <div>{moneyFmt(lineNet)}</div>
                        </div>
                      ) : (
                        <>{moneyFmt(lineNet)}</>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-bold">{period === "one_time" ? <span className="text-[#A3A3A3]">—</span> : <>{moneyFmt(annualNet)}</>}</td>
                    <td className="px-3 py-2 text-right font-black text-[#0055FF]">{it.status === "active" ? <>{moneyFmt(runningArr)}</> : <span className="text-[#A3A3A3]">—</span>}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase ${it.status === "active" ? "text-[#16A34A]" : "text-[#A3A3A3]"}`}>{it.status}</span></td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-right">
                        {/* Rows seeded by activating a specification are
                            derived commercial history. Deleting one would
                            desynchronise the ledger from the specification
                            that produced it, so the ordinary UI does not
                            offer it — only hand-entered rows stay deletable. */}
                        {canPermanentlyDelete(it) ? (
                          <button
                            onClick={(e) => deleteItem(it.id, it.description, e)}
                            className="text-[#DC2626] hover:bg-[#FEF2F2] p-1"
                            title="Delete permanently"
                            data-testid={`delete-recurring-${it.id}`}
                          >
                            <Trash size={12} />
                          </button>
                        ) : (
                          <span className="text-[#D4D4D4] p-1 inline-flex" title="From a billing specification — part of the commercial record"
                                data-testid={`delete-locked-${it.id}`}>
                            <Lock size={12} />
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              });
            })()}
          </tbody>
          {activeItems.length > 0 && (
            <tfoot>
              {creditTotal < 0 && (
                <>
                  <tr className="border-t-2 border-[#0A0A0A] bg-[#FAFAFA]" data-testid="billing-footer-gross">
                    <td colSpan={10} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-[#525252]">Gross ARR (before credits)</td>
                    <td className="px-3 py-2 text-right text-sm text-[#525252] line-through">{currency} {grossArr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td colSpan={isAdmin ? 3 : 2}></td>
                  </tr>
                  <tr className="bg-[#FAFAFA]" data-testid="billing-footer-discount">
                    <td colSpan={10} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-[#DC2626]">Recurring Credits / Discounts</td>
                    <td className="px-3 py-2 text-right text-sm font-bold text-[#DC2626]">{currency} {creditTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td colSpan={isAdmin ? 3 : 2}></td>
                  </tr>
                </>
              )}
              <tr className={`bg-[#FAFAFA] ${creditTotal < 0 ? "border-t border-[#0A0A0A]" : "border-t-2 border-[#0A0A0A]"}`}>
                <td colSpan={10} className="px-3 py-2 text-right font-bold text-[11px] uppercase tracking-wider">{creditTotal < 0 ? "Net Annual Recurring Revenue" : "Annual Recurring Revenue"}</td>
                <td className="px-3 py-2 text-right font-black text-sm" data-testid="billing-arr-footer">{currency} {annualTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td colSpan={isAdmin ? 3 : 2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Events timeline */}
      <div className="border border-[#E5E5E5] bg-white">
        <div className="bg-[#FAFAFA] px-4 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-bold text-[#0A0A0A]">Billing Event Log</h3>
          <p className="text-[10px] text-[#A3A3A3] mt-0.5">Every renewal / proration draft pushed to FinOps.</p>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
              <th className="px-3 py-2 text-left font-bold">Type</th>
              <th className="px-3 py-2 text-left font-bold">Invoice</th>
              <th className="px-3 py-2 text-left font-bold">Period</th>
              <th className="px-3 py-2 text-right font-bold">Amount</th>
              <th className="px-3 py-2 text-left font-bold">Created</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#A3A3A3]">No billing events yet.</td></tr>}
            {events.map(ev => (
              <tr key={ev.id} className="border-b border-[#F5F5F5]" data-testid={`billing-event-${ev.id}`}>
                <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${ev.event_type === "renewal" ? "bg-[#EEF2FF] text-[#4338CA]" : ev.event_type === "proration" ? "bg-[#FFFBEB] text-[#D97706]" : "bg-[#F0FDF4] text-[#16A34A]"}`}>{ev.event_type}</span></td>
                <td className="px-3 py-2 font-mono text-[11px]">{ev.finops_invoice_number || "—"}</td>
                <td className="px-3 py-2 text-[#525252]">{ev.period_start} → {ev.period_end}</td>
                <td className="px-3 py-2 text-right font-bold">{Number(ev.total_amount || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{ev.created_at ? ev.created_at.slice(0, 10) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* v1.66 — Draft renewal modal with anniversary override.
          The default date comes from `preview.anniversary` (the
          system-snapped date — typically the contract expiry day).
          The operator can pick any future date — useful for pro-forma
          invoices the client wants dated independently of the
          renewal cycle, or for catching up on missed periods. */}
      <Dialog open={renewalOpen}
              onOpenChange={(o) => { if (!renewalSubmitting) setRenewalOpen(o); }}>
        <DialogContent data-testid="renewal-modal" className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Draft renewal invoice</DialogTitle>
            <DialogDescription>
              Creates a DRAFT invoice in FinOps for the period starting
              on the chosen anniversary date. Nothing is sent
              automatically — you decide when to email it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="renewal-anniv" className="text-xs uppercase tracking-wider text-neutral-500">
                Anniversary date (annual period starts on)
              </Label>
              <Input
                id="renewal-anniv"
                data-testid="renewal-anniv-input"
                type="date"
                value={renewalDate}
                onChange={(e) => setRenewalDate(e.target.value)}
                className="mt-1"
              />
              <div className="text-[11px] text-neutral-500 mt-1">
                System default: <code>{preview?.anniversary || "—"}</code>.
                Pick a different date for pro-forma billing.
              </div>
            </div>
            <div>
              <Label htmlFor="renewal-issue" className="text-xs uppercase tracking-wider text-neutral-500">
                Issue date (invoice date)
              </Label>
              <Input
                id="renewal-issue"
                data-testid="renewal-issue-input"
                type="date"
                value={renewalIssueDate}
                onChange={(e) => setRenewalIssueDate(e.target.value)}
                className="mt-1"
              />
              <div className="text-[11px] text-neutral-500 mt-1">
                Defaults to today. Backend will clamp to no earlier
                than 42 days ahead of the anniversary.
              </div>
            </div>
            {/* v1.69 — Inline diagnostic. Runs every step of the
                renewal flow WITHOUT writing anything, then displays
                the precise failing step. Use this when the regular
                "Draft invoice" button returns a 500 so we can see
                the underlying database/SQL error instead of just a
                bare HTTP code. */}
            {diagnosis && (
              <div
                data-testid="renewal-diagnosis-panel"
                className={`rounded-md border p-3 text-xs space-y-1 ${
                  diagnosis.ok
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-rose-200 bg-rose-50 text-rose-900"
                }`}
              >
                <div className="font-bold">
                  {diagnosis.ok
                    ? "Dry-run passed — every step would succeed."
                    : `Failed at: ${diagnosis.failed_at || "unknown"}`}
                </div>
                {!diagnosis.ok && diagnosis.error && (
                  <div className="font-mono text-[11px] whitespace-pre-wrap break-all">
                    {diagnosis.error}
                  </div>
                )}
                <details>
                  <summary className="cursor-pointer underline">
                    Show all {diagnosis.steps?.length || 0} steps · build {diagnosis.code_build}
                  </summary>
                  <ul className="mt-2 space-y-0.5">
                    {(diagnosis.steps || []).map((s, i) => (
                      <li key={i} className="flex gap-2">
                        <span>{s.ok ? "✓" : "✗"}</span>
                        <span className="font-mono">{s.step}</span>
                        {s.detail && (
                          <span className="text-neutral-600">— {s.detail}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenewalOpen(false)}
              disabled={renewalSubmitting || diagnosing}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              data-testid="renewal-diagnose"
              onClick={runDiagnose}
              disabled={!renewalDate || diagnosing || renewalSubmitting}
              title="Dry-run every step — no writes. Use if the regular button errors."
            >
              {diagnosing ? "Diagnosing…" : "Diagnose"}
            </Button>
            <Button
              data-testid="renewal-submit"
              onClick={submitRenewal}
              disabled={!renewalDate || renewalSubmitting || diagnosing}
              className="bg-[#0055FF] hover:bg-[#003ECC] text-white"
            >
              {renewalSubmitting ? "Drafting…" : "Draft invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Billing area shell — the four commercial surfaces, in the order an
 * operator actually asks the questions:
 *   1. What did we originally sell them?
 *   2. What are they licensed for today, and what will renew?
 *   3. What changed during the year?
 *   4. What did each prior year look like?
 *
 * Defaults to the Current Recurring Specification because that is the
 * live commercial position and the most-used screen.
 */
const BILLING_SURFACES = [
  { id: "current", label: "Current recurring specification" },
  { id: "initial", label: "Initial specification" },
  { id: "changes", label: "Interim changes" },
  { id: "history", label: "Renewal history" },
  // Why each of the above was recorded the way it was. Deliberately its
  // own surface: evidence explains every other surface, so it belongs to
  // none of them exclusively.
  { id: "evidence", label: "Source invoices & interpretation" },
  // Recording an invoice the customer has ALREADY received. Its own
  // surface because it is a workflow rather than a view, and because it
  // must never be mistaken for raising a new charge.
  { id: "import", label: "Import invoices" },
];

export default function ClientBillingTab({ clientId, isAdmin }) {
  const [surface, setSurface] = useState("current");
  return (
    <div className="space-y-4" data-testid="client-billing-area">
      <div role="tablist" aria-label="Billing views" className="flex flex-wrap gap-0 border-b border-[#E5E5E5]">
        {BILLING_SURFACES.map((s) => (
          <button
            key={s.id}
            role="tab"
            type="button"
            aria-selected={surface === s.id}
            onClick={() => setSurface(s.id)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] ${
              surface === s.id
                ? "border-[#0055FF] text-[#0055FF]"
                : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
            }`}
            data-testid={`billing-surface-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {surface === "current" && (
          <CurrentRecurringSpecificationPanel
            clientId={clientId} isAdmin={isAdmin}
            onImportInvoice={() => setSurface("import")} />
        )}
        {surface === "initial" && (
          <InitialSpecificationPanel clientId={clientId} isAdmin={isAdmin} />
        )}
        {surface === "changes" && (
          <AmendmentsPanel clientId={clientId} isAdmin={isAdmin} />
        )}
        {surface === "history" && (
          <RenewalHistoryPanel clientId={clientId} isAdmin={isAdmin}
                               onOpenInitial={() => setSurface("initial")} />
        )}
        {surface === "evidence" && (
          <SourceDocumentsPanel clientId={clientId} isAdmin={isAdmin} />
        )}
        {surface === "import" && (
          isAdmin ? (
            /* Keyed on the surface so leaving and returning starts a
               fresh session rather than resurrecting a half-finished one
               whose figures were calculated against an older position. */
            <InvoiceImportWizard
              key={`${clientId}-import`}
              clientId={clientId}
              onDone={() => setSurface("current")} />
          ) : (
            <div className="border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-8 text-center">
              <p className="text-sm font-bold text-[#0A0A0A]">
                Importing invoices is restricted
              </p>
              <p className="text-xs text-[#525252] mt-1.5 max-w-xl mx-auto leading-relaxed">
                Recording an invoice changes the client's commercial position and
                the basis of their next renewal, so it is limited to Platform
                Admin.
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
